import _isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import Head from 'next/head';

import { getFullUserName } from '@sravni/ugc/lib/utils/getFullUserName';

import { SravniNewsModelsPicture } from '../__generated__/news/Api';
import { IMetadata } from '../interfaces/metadata';
import { IDictionaryDto, IHighlightedMaterial, IMaterial, TAuthorShort, TOrganizationData } from '../interfaces/news';
import { IGlobalState } from '../reducers';
import { getCurrentLocation } from '../selectors/locations';
import { getMaterialsList } from '../selectors/news';
import { TMetadataParams } from '../server/services/metadata';
import { makeMaterialListPageSchema, makeSingleMaterialPageSchema } from '../utils/metadata';

export type TPageType = 'list' | 'material';

export type TListInfoFields = {
  rubricName: string;
  rubricType: string;
  tagType: string;
  pageType?: TPageType;
};

export type TListMetadataParams = {
  seo: Partial<IMetadata>;
  list: IHighlightedMaterial[];
  infoFields: TListInfoFields;
};

const getOgImage = ({ rubricName, tagType }) => {
  if (tagType === 'Тесты и игры') return 'tests';

  switch (rubricName) {
    case 'Новости':
      return 'news';
    case 'Новости Спорта':
      return 'news';
    case 'Новости партнеров':
      return 'blogs';
    case 'Карты':
      return 'cards';
    case 'Вклады':
      return 'savings';
    case 'Кредиты':
      return 'credits';
    case 'Инвестиции':
      return 'investments';
    case 'Недвижимость':
      return 'realEstate';
    case 'Страховки':
      return 'insurance';
    case 'Советы':
      return 'advices';

    default:
      return 'main';
  }
};

export const makeSearchPageMetadata = (text) => {
  const title = `${text ? `${text} - ` : ''}Поиск по Журналу Сравни`;

  return { title };
};

export const makeSpecialProjectsMetadata = () => {
  const title = 'СПЕЦПРОЕКТЫ на Сравни!';
  const description = 'Заметные, яркие, понятные, конверсионные и просто классные!';

  return {
    title,
    description,
    ogTitle: title,
    ogDescription: description,
    ogImage: 'https://sravni-promo.storage.yandexcloud.net/uploads/2023/06/shareSpecialProjects.jpg',
    ogImageWidth: '1200px',
    ogImageHeight: '630px',
    breadcrumbs: [
      {
        url: '/promo/special-projects/',
        title: 'Спецпроекты',
      },
    ],
  };
};

export const makeListMetadata = (params: TListMetadataParams) => {
  const {
    seo,
    list,
    infoFields,
    infoFields: { rubricName, rubricType, tagType },
  } = params;

  const title = rubricName || tagType ? `Новости: ${rubricName || tagType}` : 'Новости';
  const rubricPath = rubricName ? `/rubrika/${rubricType}/` : '';
  const tagPath = tagType ? `/teg/${tagType}/` : '';
  const breadcrumbs = [
    {
      url: '/mag/',
      title: 'Новости',
    },
  ];

  const url = `https://${process.env.WEB_PATH}/mag${rubricPath || tagPath}`;
  const schema = makeMaterialListPageSchema(list, { url, title });
  const ogImage = getOgImage(infoFields);

  return {
    title: seo?.title || title,
    description: seo?.description || title,
    ogTitle: seo?.ogTitle || seo?.title || title,
    ogDescription: seo?.ogDescription || seo?.description || title,
    ogImage: require(`../../public/og/${ogImage}.jpg`),
    ogImageWidth: '1200px',
    ogImageHeight: '630px',
    breadcrumbs: seo?.breadcrumbs?.length ? seo.breadcrumbs : breadcrumbs,
    schema: {
      ...schema,
      ...seo?.schema,
    },
    prefooter: seo.prefooter,
  };
};

export const makeMaterialMetadata = (material: IMaterial, author: TAuthorShort, { rubric, rubricAlias }) => {
  const title = clearTextFromHTMLTagsAndBreak(material.title);
  const description = material.lead && clearTextFromHTMLTagsAndBreak(material.lead);

  return {
    title,
    description: description,
    ogTitle: title,
    ogDescription: description,
    breadcrumbs: [
      {
        url: '/mag/',
        title: 'Новости',
      },
      {
        url: '',
        title: title,
      },
    ],
    schema: makeSingleMaterialPageSchema(material, author, { rubric, rubricAlias, title, description }),
  };
};

export const MetaImageTags = ({ picture }: { picture: SravniNewsModelsPicture }) => {
  const { url, width, height } = picture;

  return (
    <>
      <meta property="og:image" content={url} />
      <meta property="og:image:width" content={`${width}`} />
      <meta property="og:image:height" content={`${height}`} />
    </>
  );
};

export const defaultSeoImage: SravniNewsModelsPicture = {
  url: 'https://f.sravni.ru/f/_/logo_200x200.jpg',
  width: 200,
  height: 200,
};

export const MetaArticleImageTags = ({ article }: { article: IMaterial }) => {
  if (!article) return;

  const { picture } = article;

  if (picture?.url) {
    return <MetaImageTags picture={picture} />;
  }

  return <MetaImageTags picture={defaultSeoImage} />;
};

export const makeArticleMetadata = (article: IMaterial, rubric: IDictionaryDto, link) => {
  const [pathname] = link.as.split('?');

  const authorName = getFullUserName({
    firstName: article.authorName,
    lastName: article.authorLastName,
    type: 'long',
  });

  return (
    <Head>
      {article.seoTitle ? (
        <>
          <title key="title">{article.seoTitle}</title>
          <meta key="og:title" property="og:title" content={article.seoTitle} />
        </>
      ) : (
        <meta key="og:title" property="og:title" content={clearTextFromHTMLTagsAndBreak(article.title)} />
      )}
      {article?.seoDescription && (
        <>
          <meta key="description" name="description" content={article.seoDescription} />
          <meta key="og:description" property="og:description" content={article.seoDescription} />
        </>
      )}
      {article?.noIndex && <meta key="robots" name="robots" content="noindex, nofollow" />}
      <meta property="og:url" content={`https://www.sravni.ru${pathname}/`} />
      <meta property="og:type" content="article" />
      <meta property="article:section" content={rubric?.name} />
      {article.tags?.length ? article.tags.map((tag) => <meta property="article:tag" content={tag} key={tag} />) : null}
      <meta property="article:" content={authorName} />
      <meta property="article:published_time" content={article.publishDate} />
      <meta property="article:modified_time" content={article.lastChangeDate} />
      <MetaArticleImageTags article={article} />
    </Head>
  );
};

export const prepareSeoData = (
  seo: IMetadata,
  state: IGlobalState,
  { rubricName, rubricType, tagType, pageType }: TListInfoFields,
) => {
  let seoData;

  if (pageType === 'list') {
    const list = getMaterialsList(state);
    seoData = makeListMetadata({ seo, list, infoFields: { rubricName, rubricType, tagType } });
  }

  return seoData;
};

export const makeSeoPayload = (
  organization: TOrganizationData,
  pathname: string,
  state: IGlobalState,
): TMetadataParams => {
  let typePage = 'insurancecompany';
  if (organization?.type === 'bank') {
    typePage = 'bank';
  } else if (organization?.type === 'mfo') {
    typePage = 'mfo';
  }
  const location = getCurrentLocation(state)?.[0];

  const organizationPayload = !_isEmpty(organization)
    ? {
        ...pick(organization, ['id', 'name', 'alias', 'type']),
        type: typePage,
      }
    : null;

  const payload = {
    url: pathname,
    ...(location && { location: pick(location, ['id', 'name', 'alias', 'route']) }),
    ...(organizationPayload && { organization: organizationPayload }),
  };

  // @ts-ignore
  return payload;
};

export const preparePath = (pathname: string) => {
  // remove search params
  pathname = pathname.split('?')[0];
  // remove hash params
  pathname = pathname.split('#')[0];
  // remove closing slash
  return pathname.endsWith('/') ? pathname : `${pathname}/`;
};

export const getCurrentMaterialAliasForPathName = (pathname: string): string => {
  const pathnameArray = pathname.split('/').reverse();
  return pathnameArray[0] === '' ? pathnameArray[1] : pathnameArray[0];
};

export const clearTextFromHTMLTagsAndBreak = (text: string) => {
  return text && text.replace(/(<([^>]+)>)/gi, '');
};
